import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormArray } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { contentTypeSchemaValidatorTextFormat, prepareToSaveValidator } from 'na-lib';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-content-types-schema-validator-dialog',
    templateUrl: './content-types-schema-validator-dialog.component.html',
    styleUrl: './content-types-schema-validator-dialog.component.css',
    standalone: false
})

export class ContentTypesSchemaValidatorDialogComponent {

  validatorForm:FormGroup
  type:string
  formatoptions = Object.values(contentTypeSchemaValidatorTextFormat)
  
  constructor(public dialogRef: MatDialogRef<ContentTypesSchemaValidatorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
            private fb: FormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService ){
      dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.type = this.data?.data?.type ? this.data.data.type : undefined

    const {min, max, regex, hint, mandatory, minDaysAgo, format, lowercase, decimal, maxExclusive, minExclusive, multipleOf, values} = this.data.data ?? {}
    this.validatorForm = this.fb.group({
      min: [{value: min , disabled: false}],
      max: [ {value: max , disabled: false} ],
      format: [ {value: format ? format : undefined, disabled: false} ],
      regex: [ {value: regex ? regex : undefined, disabled: false} ],
      hint: [ {value: hint ? hint : undefined, disabled: false} ],
      mandatory: [ {value: mandatory ? mandatory : undefined, disabled: false} ],
      minDaysAgo: [ {value: minDaysAgo ? minDaysAgo : undefined, disabled: false} ],
      lowercase: [ {value: lowercase ? lowercase : undefined, disabled: false} ],
      decimal: [ {value: decimal ?? undefined, disabled: false} ],
      maxExclusive: [ {value: maxExclusive ? maxExclusive : undefined, disabled: false} ],
      minExclusive: [ {value: minExclusive ? minExclusive : undefined, disabled: false} ],
      multipleOf: [ {value: multipleOf ? multipleOf : undefined, disabled: false} ],
      values: [ {value: Array.isArray(values) ? values.join("\n") : typeof values === 'string' ? values : '', disabled: false} ]
    })
  }

  onCancel() {
    this.dialogRef.close([false])
  }
  onSave() {
    if (this.validatorForm.invalid || this.validationErrorKey) return;
    const min = this.validatorForm.get('min')?.value;
    const max = this.validatorForm.get('max')?.value;
    const formValue = { ...this.validatorForm.value };
    if (this.type === 'textLength') {
      if (min !== null && min !== undefined && min !== '' && Number(min) === 0) delete formValue.min;
      if (max !== null && max !== undefined && max !== '' && Number(max) === 0) delete formValue.max;
    }
    if (this.type === 'arrayLength') {
      const results: Record<string, unknown> = { type: 'arrayLength' };
      for (const bound of ['min', 'max'] as const) {
        const value = formValue[bound];
        if (value !== null && value !== undefined && value !== '') results[bound] = Number(value);
      }
      this.dialogRef.close([true, results]);
      return;
    }
    const results = prepareToSaveValidator(formValue, this.type) as Record<string, unknown>;
    if (this.type === 'numericRange' && formValue.decimal === false) results['decimal'] = false;
    if (this.type === 'textLength' || this.type === 'numericRange' || this.type === 'dateRange') {
      for (const [key, value] of [['min', min], ['max', max]] as const) {
        if (value === null || value === undefined || value === '' || (this.type === 'textLength' && Number(value) === 0)) delete results[key];
        else results[key] = Number(value);
      }
    }
    this.dialogRef.close([true, results])
  }

  get validationErrorKey(): string | null {
    if (!this.validatorForm) return null;
    const value = this.validatorForm.value;
    const present = (input: unknown): boolean => input !== null && input !== undefined && String(input).trim() !== '';
    const hasMin = present(value.min);
    const hasMax = present(value.max);
    const min = Number(value.min);
    const max = Number(value.max);

    if (this.type === 'arrayLength') {
      if ((hasMin && (!Number.isInteger(min) || min < 0)) || (hasMax && (!Number.isInteger(max) || max < 0))) {
        return 'contentTypes.schemaRuleInvalidItemCount';
      }
      if (hasMin && hasMax && min > max) return 'contentTypes.schemaRuleInvalidRange';
      return hasMin || hasMax ? null : 'contentTypes.schemaRuleEmpty';
    }

    if (this.type === 'textLength' || this.type === 'numericRange' || this.type === 'dateRange') {
      if ((hasMin && !Number.isFinite(min)) || (hasMax && !Number.isFinite(max))) return 'contentTypes.schemaRuleInvalidNumber';
      if (this.type === 'textLength' && ((hasMin && (!Number.isInteger(min) || min < 0)) || (hasMax && (!Number.isInteger(max) || max < 0)))) {
        return 'contentTypes.schemaRuleInvalidNumber';
      }
      const effectiveMin = hasMin && (this.type !== 'textLength' || min > 0);
      const effectiveMax = hasMax && (this.type !== 'textLength' || max > 0);
      if (effectiveMin && effectiveMax && (min > max || (min === max && this.type === 'numericRange' && (value.minExclusive || value.maxExclusive)))) {
        return 'contentTypes.schemaRuleInvalidRange';
      }
      if (hasMin && hasMax && min === 0 && max === 0) return 'contentTypes.schemaRuleEmpty';
      if (this.type === 'textLength') return effectiveMin || effectiveMax ? null : 'contentTypes.schemaRuleEmpty';
      if (this.type === 'dateRange') {
        if (present(value.minDaysAgo) && (!Number.isFinite(Number(value.minDaysAgo)) || Number(value.minDaysAgo) < 0)) return 'contentTypes.schemaRuleInvalidNumber';
        return hasMin || hasMax || (present(value.minDaysAgo) && Number(value.minDaysAgo) > 0) ? null : 'contentTypes.schemaRuleEmpty';
      }
      if (present(value.multipleOf) && (!Number.isFinite(Number(value.multipleOf)) || Number(value.multipleOf) <= 0)) return 'contentTypes.schemaRuleInvalidNumber';
      return hasMin || hasMax || present(value.multipleOf) || value.decimal === false ? null : 'contentTypes.schemaRuleEmpty';
    }

    if (this.type === 'textRegex') {
      if (!present(value.regex)) return 'contentTypes.schemaRuleEmpty';
      try { new RegExp(value.regex); } catch { return 'contentTypes.schemaRuleInvalidRegex'; }
      return null;
    }
    if (this.type === 'textFormat') return present(value.format) ? null : 'contentTypes.schemaRuleEmpty';
    if (this.type === 'textEnum' || this.type === 'numericEnum') {
      const entries = String(value.values || '').split(/\r?\n/).map(entry => entry.trim()).filter(Boolean);
      if (!entries.length) return 'contentTypes.schemaRuleEmpty';
      if (this.type === 'numericEnum' && entries.some(entry => !Number.isFinite(Number(entry)))) return 'contentTypes.schemaRuleInvalidNumber';
      return null;
    }
    return this.type === 'textEMail' ? null : 'contentTypes.schemaRuleEmpty';
  }

  getValidatorLabel(): string {
    return this.translate.instant(`contentTypes.schemaRuleLabels.${this.type || 'default'}`);
  }

  getValidatorDescription(): string {
    return this.translate.instant(`contentTypes.schemaRuleDescriptions.${this.type || 'default'}`);
  }

  getMinLabel(): string {
    if (this.type === 'arrayLength') return this.translate.instant('contentTypes.schemaRuleLimits.minItems');
    if (this.type === 'textLength') return this.translate.instant('contentTypes.schemaRuleLimits.minLength');
    if (this.type === 'dateRange') return this.translate.instant('contentTypes.schemaRuleLimits.earliestDate');
    return this.translate.instant('contentTypes.schemaRuleLimits.minValue');
  }

  getMaxLabel(): string {
    if (this.type === 'arrayLength') return this.translate.instant('contentTypes.schemaRuleLimits.maxItems');
    if (this.type === 'textLength') return this.translate.instant('contentTypes.schemaRuleLimits.maxLength');
    if (this.type === 'dateRange') return this.translate.instant('contentTypes.schemaRuleLimits.latestDate');
    return this.translate.instant('contentTypes.schemaRuleLimits.maxValue');
  }

  getMinPlaceholder(): string {
    if (this.type === 'arrayLength') return '';
    if (this.type === 'textLength') return this.translate.instant('contentTypes.schemaRuleLimits.minLengthExample');
    if (this.type === 'dateRange') return this.translate.instant('contentTypes.schemaRuleLimits.earliestDateExample');
    return this.translate.instant('contentTypes.schemaRuleLimits.minValueExample');
  }

  getMaxPlaceholder(): string {
    if (this.type === 'arrayLength') return '';
    if (this.type === 'textLength') return this.translate.instant('contentTypes.schemaRuleLimits.maxLengthExample');
    if (this.type === 'dateRange') return this.translate.instant('contentTypes.schemaRuleLimits.latestDateExample');
    return this.translate.instant('contentTypes.schemaRuleLimits.maxValueExample');
  }

  getMinHint(): string {
    if (this.type === 'arrayLength') return this.translate.instant('contentTypes.schemaRuleLimits.minItemsHint');
    if (this.type === 'textLength') return this.translate.instant('contentTypes.schemaRuleLimits.minLengthHint');
    if (this.type === 'dateRange') return this.translate.instant('contentTypes.schemaRuleLimits.earliestDateHint');
    return this.translate.instant('contentTypes.schemaRuleLimits.minValueHint');
  }

  getMaxHint(): string {
    if (this.type === 'arrayLength') return this.translate.instant('contentTypes.schemaRuleLimits.maxItemsHint');
    if (this.type === 'textLength') return this.translate.instant('contentTypes.schemaRuleLimits.maxLengthHint');
    if (this.type === 'dateRange') return this.translate.instant('contentTypes.schemaRuleLimits.latestDateHint');
    return this.translate.instant('contentTypes.schemaRuleLimits.maxValueHint');
  }

}
